import React, { useState, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';

import { injectIntl } from '../../util/reactIntl';
import {
  Form,
  FieldTextInput,
  SecondaryButton,
  Button,
  FieldSelectModern,
  IconClose,
  InlineTextButton,
  FieldCheckbox,
} from '../../components';
import { required } from '../../util/validators';
import { generateMonthsList, generateYearsList } from '../../util/dates';

import css from './SignupProviderForms.css';

// Constants
const REQUIRED_FIELDS = ['customerName', 'task', 'description', 'startMonth', 'startYear'];
const ALL_REQUIRED_FIELDS = [...REQUIRED_FIELDS, 'taskType'];
const START_YEAR = 1900;

// Helper functions
const validateWorkExperience = (fieldSet = {}, skipValidation, requiredMessage) => {
  if (skipValidation) return undefined;

  const error = {};

  // Validate required fields
  REQUIRED_FIELDS.forEach(field => {
    if (!fieldSet[field]) {
      error[field] = requiredMessage(`SignupProviderForms.${field}Required`);
    }
  });

  // Validate end date if the job doesn't continue
  if (!fieldSet.jobContinues) {
    if (!fieldSet.endMonth) {
      error.endMonth = requiredMessage('SignupProviderForms.dateRequired');
    }
    if (!fieldSet.endYear) {
      error.endYear = requiredMessage('SignupProviderForms.dateRequired');
    }

    // Validate end date is after start date
    if (fieldSet.startYear && fieldSet.endYear && fieldSet.startMonth && fieldSet.endMonth) {
      const startDate = new Date(fieldSet.startYear, fieldSet.startMonth - 1);
      const endDate = new Date(fieldSet.endYear, fieldSet.endMonth - 1);
      if (endDate < startDate) {
        error.endMonth = requiredMessage('SignupProviderForms.endDateBeforeStartDate');
      }
    }
  }

  // Validate taskType
  if (!fieldSet.taskType) {
    error.taskType = requiredMessage('SignupProviderForms.marketTypeRequired');
  }

  return Object.keys(error).length > 0 ? error : undefined;
};

const areAllFieldsFilled = workExperience => {
  return ALL_REQUIRED_FIELDS.every(field => !!workExperience[field]);
};

const isAddButtonDisabled = (values, skipValidation, requiredMessage) => {
  if (skipValidation) return false;
  return values.workExperience?.some(
    item => !!validateWorkExperience(item, false, requiredMessage)
  );
};

const SignupProviderWorkExperienceForm = props => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const requiredMessage = useCallback(id => required(props.intl.formatMessage({ id })), [
    props.intl,
  ]);

  // Memoize date lists to avoid regenerating on every render
  const monthsList = useMemo(() => generateMonthsList(), []);
  const yearsList = useMemo(() => generateYearsList(START_YEAR), []);

  // Handle form submission with Enter key
  const handleKeyDown = useCallback((e, handleSubmit, form, invalid) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!invalid) {
        handleSubmit(form.getState().values, form);
      }
    }
  }, []);

  // Handle adding new work experience entry
  const handleAddWorkExperience = useCallback((push, workExperienceListLength) => {
    push('workExperience', {});
    setSelectedIndex(workExperienceListLength);
  }, []);

  // Handle removing work experience entry
  const handleRemoveWorkExperience = useCallback((remove, index) => {
    remove('workExperience', index);
    setSelectedIndex(prev => Math.max(0, prev - 1));
  }, []);

  return (
    <FinalForm
      {...props}
      mutators={{
        ...arrayMutators,
      }}
      validate={values => {
        const errors = {};

        // Ensure workExperience is always an array
        if (Array.isArray(values.workExperience)) {
          errors.workExperience = values.workExperience.map(item =>
            validateWorkExperience(item, values.dontAddWorkExperience, requiredMessage)
          );
        } else {
          errors.workExperience = [];
        }

        return errors;
      }}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          handleSubmit,
          onPrev,
          form,
          intl,
          form: {
            mutators: { push, remove },
          },
          invalid,
          values,
          isModal,
          initialValues,
        } = formRenderProps;

        const classes = classNames(rootClassName || css.root, className);

        const skipValidation = values.dontAddWorkExperience;
        const workExperienceList = values.workExperience || [];
        const selectedWorkExperience = workExperienceList[selectedIndex] || {};
        const hideEndDate = selectedWorkExperience.jobContinues;

        // Submit button logic
        const hasInvalidEntries = invalid && !skipValidation;
        const hasEntriesWhenSkipping = skipValidation && workExperienceList.length > 0;
        const submitDisabled = hasInvalidEntries || hasEntriesWhenSkipping;

        // Modal submit logic
        const hasChanges =
          JSON.stringify(initialValues.workExperience) !== JSON.stringify(values.workExperience);
        const isEmptyList = workExperienceList.length === 0;
        const allowEmptySubmit = isEmptyList && isModal;
        const modalSubmitDisabled = !allowEmptySubmit && (invalid || !hasChanges);

        // Translated labels
        const labels = {
          heading: intl.formatMessage({ id: 'SignupProviderForms.workExperienceHeading' }),
          headingDescription: intl.formatMessage({
            id: 'SignupProviderForms.workExperienceDescription',
          }),
          nextButton: intl.formatMessage({ id: 'SignupProviderForms.nextUpWorkedWith' }),
          previousButton: intl.formatMessage({ id: 'SignupProviderForms.previousButton' }),
          addButton: intl.formatMessage({ id: 'SignupProviderForms.addButton' }),
          okButton: intl.formatMessage({ id: 'SignupProviderForms.okButton' }),
          customerName: intl.formatMessage({
            id: 'SignupProviderForms.workExperienceCustomerNameLabel',
          }),
          customerNamePlaceholder: intl.formatMessage({
            id: 'SignupProviderForms.workExperienceCustomerNamePlaceholder',
          }),
          task: intl.formatMessage({ id: 'SignupProviderForms.workExperienceTaskLabel' }),
          taskPlaceholder: intl.formatMessage({
            id: 'SignupProviderForms.workExperienceTaskPlaceholder',
          }),
          startDate: intl.formatMessage({ id: 'SignupProviderForms.workExperienceStartLabel' }),
          endDate: intl.formatMessage({ id: 'SignupProviderForms.workExperienceEndLabel' }),
          monthPlaceholder: intl.formatMessage({ id: 'SignupProviderForms.monthPlaceholder' }),
          yearPlaceholder: intl.formatMessage({ id: 'SignupProviderForms.yearPlaceholder' }),
          jobContinues: intl.formatMessage({
            id: 'SignupProviderForms.workExperienceContinuesLabel',
          }),
          marketTypeB2B: intl.formatMessage({
            id: 'SignupProviderForms.workExperienceMarketTypeB2BLabel',
          }),
          marketTypeB2C: intl.formatMessage({
            id: 'SignupProviderForms.workExperienceMarketTypeB2CLabel',
          }),
          marketTypeNone: intl.formatMessage({
            id: 'SignupProviderForms.workExperienceMarketTypeNoTypeLabel',
          }),
          marketTypeRequired: intl.formatMessage({
            id: 'SignupProviderForms.marketTypeRequired',
          }),
          marketTypeLabel: intl.formatMessage({
            id: 'SignupProviderForms.workExperienceMarketTypeLabel',
          }),
          description: intl.formatMessage({
            id: 'SignupProviderForms.workExperienceDescriptionLabel',
          }),
          descriptionPlaceholder: intl.formatMessage({
            id: 'SignupProviderForms.workExperienceDescriptionPlaceholder',
          }),
          skipWorkExperience: intl.formatMessage({
            id: 'SignupProviderForms.workExperienceDontAddLabel',
          }),
          incompleteFields: intl.formatMessage({
            id: 'SignupProviderForms.incompleteFieldsMessage',
          }),
        };

        const isSelectedWorkExperienceComplete = areAllFieldsFilled(selectedWorkExperience);

        return (
          <div className={classes}>
            <Form className={css.form} onSubmit={values => handleSubmit(values, form)}>
              <div className={css.formFieldsContainer}>
                <h2 className={css.mainHeadingNoMargin}>{labels.heading}</h2>
                <div className={css.workExperienceInfoDescription}>{labels.headingDescription}</div>
                <div className={css.twoColumnLayout}>
                  <div className={css.leftColumn}>
                    <ul className={css.workExperienceList}>
                      {workExperienceList.length > 0 &&
                        workExperienceList.map((experience, index) => {
                          const isValid = !validateWorkExperience(
                            experience,
                            skipValidation,
                            requiredMessage
                          );

                          return (
                            <div key={index} className={css.workExperienceItemContainer}>
                              <li
                                className={classNames(css.workExperienceItem, {
                                  [css.selected]: index === selectedIndex,
                                  [css.invalid]: !isValid,
                                })}
                                onClick={() => setSelectedIndex(index)}
                              >
                                <div className={css.companyInfoContainer}>
                                  <div>{experience?.customerName || labels.customerName}</div>
                                  <div>{experience?.task || labels.task}</div>
                                </div>
                                <span
                                  className={css.deleteIconContainer}
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleRemoveWorkExperience(remove, index);
                                  }}
                                >
                                  <IconClose rootClassName={css.deleteIcon} />
                                </span>
                              </li>
                            </div>
                          );
                        })}
                    </ul>

                    <div className={css.column}>
                      <InlineTextButton
                        type="button"
                        className={
                          isAddButtonDisabled(values, skipValidation, requiredMessage)
                            ? css.addToArrayDisabled
                            : css.addToArray
                        }
                        onClick={() => handleAddWorkExperience(push, workExperienceList.length)}
                        disabled={isAddButtonDisabled(values, skipValidation, requiredMessage)}
                      >
                        {labels.addButton}
                      </InlineTextButton>
                      {!isModal && (
                        <FieldCheckbox
                          id="dontAddWorkExperience"
                          name="dontAddWorkExperience"
                          label={labels.skipWorkExperience}
                          defaultValue={false}
                          onKeyDown={e => handleKeyDown(e, handleSubmit, form, invalid)}
                        />
                      )}
                    </div>
                  </div>
                  {workExperienceList.length > 0 && (
                    <div className={css.rightColumn}>
                      {!isSelectedWorkExperienceComplete && (
                        <div className={css.incompleteFieldsMessage}>{labels.incompleteFields}</div>
                      )}
                      <FieldTextInput
                        id={`workExperience.${selectedIndex}.customerName`}
                        name={`workExperience.${selectedIndex}.customerName`}
                        type="text"
                        label={labels.customerName}
                        placeholder={labels.customerNamePlaceholder}
                        disabled={skipValidation}
                        onKeyDown={e => handleKeyDown(e, handleSubmit, form, invalid)}
                      />
                      <FieldTextInput
                        id={`workExperience.${selectedIndex}.task`}
                        name={`workExperience.${selectedIndex}.task`}
                        type="text"
                        label={labels.task}
                        placeholder={labels.taskPlaceholder}
                        disabled={skipValidation}
                        onKeyDown={e => handleKeyDown(e, handleSubmit, form, invalid)}
                      />

                      <div className={css.datesContainer}>
                        <FieldSelectModern
                          id={`workExperience.${selectedIndex}.startMonth`}
                          name={`workExperience.${selectedIndex}.startMonth`}
                          options={monthsList}
                          placeholder={labels.monthPlaceholder}
                          label={labels.startDate}
                          disabled={skipValidation}
                          onKeyDown={e => handleKeyDown(e, handleSubmit, form, invalid)}
                        />
                        <FieldSelectModern
                          id={`workExperience.${selectedIndex}.startYear`}
                          name={`workExperience.${selectedIndex}.startYear`}
                          options={yearsList}
                          placeholder={labels.yearPlaceholder}
                          label={labels.startDate}
                          disabled={skipValidation}
                          onKeyDown={e => handleKeyDown(e, handleSubmit, form, invalid)}
                        />
                        {!hideEndDate && (
                          <>
                            <FieldSelectModern
                              id={`workExperience.${selectedIndex}.endMonth`}
                              name={`workExperience.${selectedIndex}.endMonth`}
                              options={monthsList}
                              placeholder={labels.monthPlaceholder}
                              label={labels.endDate}
                              disabled={skipValidation}
                              onKeyDown={e => handleKeyDown(e, handleSubmit, form, invalid)}
                            />
                            <FieldSelectModern
                              id={`workExperience.${selectedIndex}.endYear`}
                              name={`workExperience.${selectedIndex}.endYear`}
                              options={yearsList}
                              placeholder={labels.yearPlaceholder}
                              label={labels.endDate}
                              disabled={skipValidation}
                              onKeyDown={e => handleKeyDown(e, handleSubmit, form, invalid)}
                            />
                          </>
                        )}
                      </div>
                      <FieldCheckbox
                        id={`workExperience.${selectedIndex}.jobContinues`}
                        name={`workExperience.${selectedIndex}.jobContinues`}
                        label={labels.jobContinues}
                        disabled={skipValidation}
                        onKeyDown={e => handleKeyDown(e, handleSubmit, form, invalid)}
                      />
                      <FieldTextInput
                        id={`workExperience.${selectedIndex}.description`}
                        name={`workExperience.${selectedIndex}.description`}
                        className={css.textFieldMedium}
                        type="textarea"
                        label={labels.description}
                        placeholder={labels.descriptionPlaceholder}
                        disabled={skipValidation}
                        onKeyDown={e => handleKeyDown(e, handleSubmit, form, invalid)}
                      />
                      <div className={css.workingMethodsContainer}>
                        <label className={css.label}>{labels.marketTypeLabel}</label>
                        <div className={css.workingMethods}>
                          <FieldCheckbox
                            id={`workExperience.${selectedIndex}.marketTypeOption1`}
                            name={`workExperience.${selectedIndex}.taskType`}
                            label={labels.marketTypeB2B}
                            value="B2B"
                            validate={required(labels.marketTypeRequired)}
                            disabled={skipValidation}
                            onKeyDown={e => handleKeyDown(e, handleSubmit, form, invalid)}
                          />
                          <FieldCheckbox
                            id={`workExperience.${selectedIndex}.marketTypeOption2`}
                            name={`workExperience.${selectedIndex}.taskType`}
                            label={labels.marketTypeB2C}
                            value="B2C"
                            validate={required(labels.marketTypeRequired)}
                            disabled={skipValidation}
                            onKeyDown={e => handleKeyDown(e, handleSubmit, form, invalid)}
                          />
                          <FieldCheckbox
                            id={`workExperience.${selectedIndex}.marketTypeOption3`}
                            name={`workExperience.${selectedIndex}.taskType`}
                            label={labels.marketTypeNone}
                            value="noType"
                            validate={required(labels.marketTypeRequired)}
                            disabled={skipValidation}
                            onKeyDown={e => handleKeyDown(e, handleSubmit, form, invalid)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className={css.submitContainer}>
                {!isModal && (
                  <SecondaryButton rootClassName={css.backButton} onClick={onPrev}>
                    {labels.previousButton}
                  </SecondaryButton>
                )}
                {!isModal && (
                  <Button rootClassName={css.nextButton} type="Submit" disabled={submitDisabled}>
                    {labels.nextButton}
                  </Button>
                )}
                {isModal && (
                  <div className={css.submitContainerModal}>
                    <Button
                      rootClassName={css.nextButton}
                      type="Submit"
                      disabled={modalSubmitDisabled}
                    >
                      {labels.okButton}
                    </Button>
                  </div>
                )}
              </div>
            </Form>
          </div>
        );
      }}
    />
  );
};

SignupProviderWorkExperienceForm.defaultProps = {
  isModal: false,
};

export default injectIntl(SignupProviderWorkExperienceForm);
